import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  CloudLightning, 
  Shield, 
  Users, 
  CheckCircle, 
  ArrowRight, 
  MapPin, 
  Phone,
  BadgeCheck,
  Heart,
  Handshake,
  Target,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LeadForm from "@/components/LeadForm";
import useSEO from "@/hooks/useSEO";

// Hero with prominent form
const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center bg-navy overflow-hidden"
    >
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1702910682879-b5bea9c004f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxzdG9ybSUyMGNsb3VkcyUyMG1vb2R5JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4Mjc3MjM1MXww&ixlib=rb-4.1.0&q=85')`
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/95 to-navy/90" />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Message */}
          <div>
            <div className="inline-flex items-center gap-2 bg-orange/20 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
              <MapPin className="w-4 h-4" />
              Minnesota Storm Damage Resource
            </div>
            
            <h1 
              data-testid="hero-title"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-tight mb-6"
            >
              Storm Damage?<br />
              <span className="text-orange">We're Here to Help.</span>
            </h1>
            
            <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-lg">
              Get connected with trusted Minnesota roofing professionals. 
              No pressure. No obligation. Just honest guidance when you need it most.
            </p>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-6 text-gray-400">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-orange" />
                <span>Free Service</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-orange" />
                <span>24hr Response</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-orange" />
                <span>Vetted Contractors</span>
              </div>
            </div>
          </div>
          
          {/* Right: Form - FRONT AND CENTER */}
          <div className="lg:pl-8">
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl">
              <div className="text-center mb-6">
                <h2 className="font-heading text-2xl font-semibold text-navy mb-2">
                  Get Started Now
                </h2>
                <p className="text-gray-600">
                  Tell us about your situation
                </p>
              </div>
              <LeadForm compact={true} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// WHY Section - Start with Why
const WhySection = () => {
  return (
    <section data-testid="why-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-orange/10 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
            <Heart className="w-4 h-4" />
            Why We Do This
          </div>
          
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-navy tracking-tight mb-6">
            Because Homeowners Deserve Better
          </h2>
          
          <p className="text-xl text-gray-600 leading-relaxed">
            After a storm, you shouldn't have to worry about finding a trustworthy contractor. 
            Too many homeowners get taken advantage of by fly-by-night operations and high-pressure sales tactics. 
            We believe there's a better way.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              icon: Shield,
              title: "Protect Homeowners",
              description: "We help you avoid scams, storm chasers, and contractors who disappear after getting paid."
            },
            {
              icon: Handshake,
              title: "Build Trust",
              description: "We only work with contractors who have proven track records and stand behind their work."
            },
            {
              icon: Heart,
              title: "Simplify the Process",
              description: "Storm damage is stressful enough. We make getting help simple and straightforward."
            }
          ].map((item) => (
            <div 
              key={item.title}
              className="text-center p-8 rounded-2xl bg-gray-50 hover:bg-orange/5 transition-colors"
            >
              <div className="w-16 h-16 bg-orange/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <item.icon className="w-8 h-8 text-orange" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-navy mb-3">
                {item.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// HOW Section - How We Do It
const HowSection = () => {
  const steps = [
    {
      number: "1",
      title: "Tell Us Your Situation",
      description: "Fill out a quick form with your contact info and what's going on with your property."
    },
    {
      number: "2",
      title: "We Review & Match",
      description: "We review your needs and connect you with a vetted local contractor who can help."
    },
    {
      number: "3",
      title: "Get Expert Help",
      description: "A trusted professional reaches out to assess your situation and provide guidance."
    }
  ];

  return (
    <section data-testid="how-section" className="py-20 bg-navy">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/90 text-sm font-medium px-4 py-2 rounded-full mb-6">
            <Target className="w-4 h-4 text-orange" />
            How It Works
          </div>
          
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-4">
            Simple. Fast. Free.
          </h2>
          
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Three easy steps to getting the help you need
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step) => (
            <div key={step.number} className="relative">
              <div className="bg-navy-light border border-navy-border rounded-2xl p-8 h-full text-center">
                <div className="w-14 h-14 bg-orange rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-heading font-bold text-white">{step.number}</span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {step.description}
                </p>
              </div>
              
              {parseInt(step.number) < steps.length && (
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                  <ArrowRight className="w-8 h-8 text-orange/50" />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a href="#get-started">
            <Button className="bg-orange hover:bg-orange-hover text-white px-8 py-6 text-lg">
              <Zap className="w-5 h-5 mr-2" />
              Get Started Now
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// WHAT Section - What We Do
const WhatSection = () => {
  const services = [
    {
      icon: CloudLightning,
      title: "Storm Damage Assessment",
      description: "Not sure if your roof was damaged? We connect you with pros who can evaluate your property."
    },
    {
      icon: Shield,
      title: "Vetted Contractor Connections",
      description: "We only work with licensed, insured Minnesota contractors with proven reputations."
    },
    {
      icon: Users,
      title: "Insurance Guidance",
      description: "Working through a claim? We connect you with contractors experienced in insurance projects."
    },
    {
      icon: BadgeCheck,
      title: "Insurance Agent Referrals",
      description: "Looking for a new insurance agent? We can point you toward trusted professionals."
    }
  ];

  return (
    <section data-testid="what-section" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-navy/10 text-navy text-sm font-medium px-4 py-2 rounded-full mb-6">
            <Zap className="w-4 h-4" />
            What We Do
          </div>
          
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-navy tracking-tight mb-4">
            Your Storm Damage Resource
          </h2>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We help Minnesota homeowners navigate storm damage with confidence
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service) => (
            <div 
              key={service.title}
              className="bg-white border border-gray-200 rounded-xl p-6 hover:border-orange/30 hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 bg-orange/10 rounded-xl flex items-center justify-center mb-4">
                <service.icon className="w-6 h-6 text-orange" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-navy mb-2">
                {service.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Insurance Claims Made Simple Section
const InsuranceClaimsSection = () => {
  const benefits = [
    "Free Roof Inspections",
    "Storm Damage Assessments",
    "Insurance Claim Guidance",
    "Detailed Documentation & Photos",
    "Adjuster Meeting Assistance",
    "Local Minnesota Roofing Experts"
  ];

  const carriers = [
    { name: "State Farm", link: "https://www.statefarm.com/claims" },
    { name: "American Family", link: "https://www.amfam.com/claims" },
    { name: "Allstate", link: "https://www.allstate.com/claims" },
    { name: "Farmers", link: "https://www.farmers.com/claims/" },
    { name: "Progressive", link: "https://www.progressive.com/claims/" },
    { name: "Liberty Mutual", link: "https://www.libertymutual.com/claims" },
    { name: "Travelers", link: "https://www.travelers.com/claims" },
    { name: "USAA", link: "https://www.usaa.com/inet/wc/claims-center" },
    { name: "Nationwide", link: "https://www.nationwide.com/claims/" },
    { name: "Auto-Owners", link: "https://www.auto-owners.com/claims" },
    { name: "Western National", link: "https://www.wnins.com/claims" },
    { name: "Farm Bureau", link: "https://www.fbfs.com/claims" },
  ];

  return (
    <section data-testid="insurance-claims-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-orange/10 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
            <Shield className="w-4 h-4" />
            Insurance Support
          </div>
          
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-navy tracking-tight mb-4">
            Insurance Claims Made Simple
          </h2>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            We Work With All Major Insurance Carriers Across Minnesota
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          {/* Left: Description */}
          <div>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Storm damage is stressful enough. Navigating the insurance process shouldn't be.
            </p>
            <p className="text-gray-600 leading-relaxed mb-8">
              At StormCare, we work with homeowners and insurance providers throughout Minnesota 
              to help make the roof restoration process as smooth as possible. Whether you've already 
              filed a claim or are just beginning to assess storm damage, our team can help guide you 
              through the process from inspection to project completion.
            </p>

            {/* Benefits Grid */}
            <div className="grid sm:grid-cols-2 gap-3">
              {benefits.map((benefit) => (
                <div key={benefit} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                  <CheckCircle className="w-5 h-5 text-orange flex-shrink-0" />
                  <span className="text-navy font-medium text-sm">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Carrier Grid */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <h3 className="font-heading text-lg font-semibold text-navy mb-2">
              We've Worked With Homeowners Insured By:
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Our team understands the roofing restoration process and can help ensure you have the 
              information needed to move forward confidently.
            </p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {carriers.map((carrier) => (
                <a
                  key={carrier.name}
                  href={carrier.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-gray-200 rounded-lg p-3 text-center hover:border-orange/50 hover:shadow-md transition-all group"
                >
                  <span className="text-sm font-medium text-navy group-hover:text-orange transition-colors">
                    {carrier.name}
                  </span>
                </a>
              ))}
            </div>
            
            <p className="text-xs text-gray-500 mt-4 text-center">
              Don't see your insurance company? We work with virtually all carriers.{" "}
              <Link to="/insurance-claims" className="text-orange hover:underline">
                Learn more
              </Link>
            </p>
          </div>
        </div>

        {/* CTA Box */}
        <div className="bg-navy rounded-2xl p-8 md:p-10">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-white mb-4">
                Not Sure If You Should File a Claim?
              </h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                Many homeowners aren't sure whether their roof damage is severe enough to involve insurance. 
                Before filing a claim, schedule a free StormCare roof inspection. We'll assess your roof, 
                document any visible storm damage, explain your options, and help you make an informed decision.
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-300">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange flex-shrink-0" />
                  Free Inspection
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange flex-shrink-0" />
                  Storm Damage Assessment
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange flex-shrink-0" />
                  Honest Recommendations
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange flex-shrink-0" />
                  No Obligation
                </span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
              <a href="#get-started">
                <Button className="bg-orange hover:bg-orange-hover text-white px-6 py-4 text-base w-full sm:w-auto">
                  <Zap className="w-4 h-4 mr-2" />
                  Schedule Free Inspection
                </Button>
              </a>
              <a href="tel:+1-555-STORM-MN">
                <Button variant="outline" className="border-gray-600 text-white hover:bg-white/10 px-6 py-4 text-base w-full sm:w-auto">
                  <Phone className="w-4 h-4 mr-2" />
                  Call StormCare Now
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-gray-500 text-center mt-6 max-w-4xl mx-auto">
          StormCare is not a public insurance adjuster and does not determine insurance coverage, 
          claim approval, or claim value. Coverage decisions are made solely by your insurance carrier 
          according to the terms and conditions of your policy.
        </p>
      </div>
    </section>
  );
};

// Trust Section
const TrustSection = () => {
  const points = [
    "No storm chasers or fly-by-night operations",
    "Licensed, insured Minnesota contractors only",
    "No high-pressure sales tactics",
    "Your decision, always"
  ];

  return (
    <section data-testid="trust-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-orange/10 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
              <BadgeCheck className="w-4 h-4" />
              Our Promise
            </div>
            
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-6">
              We Only Work With the Most Reputable Contractors in Minnesota
            </h2>
            
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Every contractor in our network is vetted for licensing, insurance, reputation, 
              and commitment to Minnesota homeowners. We don't work with anyone we wouldn't 
              recommend to our own family.
            </p>

            <ul className="space-y-4">
              {points.map((point) => (
                <li key={point} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-orange flex-shrink-0" />
                  <span className="text-navy font-medium">{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-navy rounded-2xl p-10">
            <div className="text-6xl text-orange/20 font-serif leading-none mb-4">"</div>
            <p className="text-2xl text-white font-heading leading-relaxed mb-6">
              The best compliment we receive is when an insurance agent trusts us enough to recommend their own client.
            </p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange/20 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-orange" />
              </div>
              <div>
                <div className="text-white font-medium">Minnesota Storm Damage</div>
                <div className="text-gray-400 text-sm">Resource Center</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const CTASection = () => {
  return (
    <section id="get-started" data-testid="cta-section" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 shadow-lg">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              Fill out the form below and we'll connect you with a trusted Minnesota contractor within 24 hours.
            </p>
          </div>
          
          <LeadForm />
        </div>
      </div>
    </section>
  );
};

export default function HomePage() {
  useSEO();
  
  return (
    <div data-testid="home-page">
      <HeroSection />
      <WhySection />
      <HowSection />
      <WhatSection />
      <InsuranceClaimsSection />
      <TrustSection />
      <CTASection />
    </div>
  );
}
